import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Users, Heart, Globe, Zap } from 'lucide-react';
import GeometricBackground from '@/components/GeometricBackground';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const AboutPage: React.FC = () => {
  const values = [
    {
      icon: Heart,
      title: 'Student-Centric',
      description:
        'We believe every feature should ultimately benefit the student learning experience.',
    },
    {
      icon: Zap,
      title: 'Innovation',
      description: 'Pushing the boundaries of what music education technology can achieve.',
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Building a supportive network of educators and learners worldwide.',
    },
  ] as const;

  const team = [
    {
      name: 'Sarah Jenkins',
      role: 'CEO & Founder',
      initials: 'SJ',
      color: 'from-purple-500 to-indigo-500',
    },
    {
      name: 'David Chen',
      role: 'CTO',
      initials: 'DC',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      name: 'Maria Garcia',
      role: 'Head of Education',
      initials: 'MG',
      color: 'from-pink-500 to-rose-500',
    },
    {
      name: 'James Wilson',
      role: 'Lead Designer',
      initials: 'JW',
      color: 'from-amber-500 to-orange-500',
    },
  ] as const;

  return (
    <>
      <Helmet>
        <title>About Us - Arrangelly</title>
        <meta
          name="description"
          content="Learn about Arrangelly's mission to empower music teachers worldwide."
        />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden">
        <GeometricBackground />

        <section className="relative pt-20 pb-20 lg:pt-32">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold mb-6">
                <span className="text-white">About </span>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                  Arrangelly
                </span>
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                We're on a mission to transform music education by providing teachers with
                professional, powerful, and beautiful tools.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-20 relative z-10">
          <div className="container mx-auto px-4">
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-8 md:p-12 max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    Arrangelly was born from a simple observation: music teachers spend too much
                    time on administration and not enough time teaching.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    We built a platform that handles the business side of your studio—payments,
                    scheduling, communication—so you can focus on what you do best: inspiring the
                    next generation of musicians.
                  </p>
                </div>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary opacity-20 blur-2xl rounded-full" />
                  <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 rounded-xl p-8 text-center">
                    <Globe className="w-16 h-16 text-accent mx-auto mb-4" />
                    <div className="text-4xl font-bold text-white mb-2">1,000+</div>
                    <div className="text-gray-400">Teachers Empowered</div>
                    <div className="my-6 h-px bg-white/10" />
                    <div className="text-4xl font-bold text-white mb-2">50+</div>
                    <div className="text-gray-400">Countries Reached</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 relative z-10">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Core Values</h2>
              <p className="text-gray-400">The principles that guide everything we build.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-8 hover:bg-white/10 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                    <value.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                  <p className="text-gray-400">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 relative z-10">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Meet the Team</h2>
              <p className="text-gray-400">Musicians and developers working in harmony.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {team.map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center group"
                >
                  <div
                    className={`w-32 h-32 mx-auto rounded-full bg-gradient-to-br ${member.color} p-1 mb-6 shadow-lg group-hover:scale-105 transition-transform duration-300`}
                  >
                    <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">{member.initials}</span>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">{member.name}</h3>
                  <p className="text-primary text-sm">{member.role}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 relative z-10">
          <div className="container mx-auto px-4">
            <div className="bg-gradient-to-r from-primary/20 to-secondary/20 backdrop-blur-lg border border-white/10 rounded-2xl p-12 text-center max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to transform your studio?
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Join thousands of music teachers who have already switched to Arrangelly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  asChild
                  className="h-12 px-8 bg-white text-primary hover:bg-gray-100 font-semibold text-lg"
                >
                  <Link to="/pricing">Get Started</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="h-12 px-8 border-white/30 text-white hover:bg-white/10 text-lg"
                >
                  <Link to="/contact">Contact Sales</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;


